import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core"
import { GetApp } from "@material-ui/icons"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import ChevronRightIcon from "@material-ui/icons/ChevronRight"
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import AppContext from "~/contexts/App"
import PropTypes from "prop-types"
import React, { useContext, useMemo, useRef, useState } from "react"

function BasicTable({
  columns,
  data,
  columnActions,
  downloadCSV,
  isFetching,
  currentPage,
  setCurrentPage,
  fetchNextPage,
  refetch,
  hasNextPage,
  pageSize,
  setPageSize,
  tableRef,
}) {
  // const [currentPage, setCurrentPage] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const wrapperRef = useRef()
  const [sorting, setSorting] = useState([])

  const { openDialog } = useContext(AppContext)

  let today = new Date().toISOString().slice(0, 10)

  const tableData = useMemo(() => {
    return data?.pages[currentPage]?.users
  }, [data, currentPage])

  const hasPreviousPage = useMemo(() => {
    return currentPage === 0 ? false : true
  }, [currentPage])

  const { getHeaderGroups, getRowModel } = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  })

  const handleChange = (event) => {
    let value = event.target.value
    value !== 1 ? setPageSize(Number(value)) : setPageSize(1)
  }

  const handleOpenExportMenu = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseExport = () => {
    setAnchorEl(null)
  }

  const renderLoading = () => {
    if (isFetching) {
      return (
        <div className="relative top-0 left-0 h-full w-full z-20 flex flex-col items-center justify-center gap-4">
          <CircularProgress size={50} />
          <p className="text-xl">Loading...</p>
        </div>
      )
    } else if (tableData?.length === 0) {
      return (
        <div className="relative top-0 left-0 h-full w-full z-20 flex flex-col items-center justify-center gap-4">
          <p className="text-2xl">No Results with this sort</p>
        </div>
      )
    }
    return null
  }

  const renderPageCounts = () => {
    const page = data?.pages[currentPage]
    if (!page) {
      return null
    }
    const start = currentPage * pageSize
    if (pageSize === 1) {
      return (
        <p>
          1 - {page?.total.toLocaleString()} out of{" "}
          {page?.total.toLocaleString()}
        </p>
      )
    }
    return (
      <>
        {page.total > 0 ? start + 1 : 0} -{" "}
        {start + pageSize < page.total ? start + pageSize : page.total} out of{" "}
        {page?.total.toLocaleString()}
      </>
    )
  }

  return (
    <div className="relative h-full">
      <div
        ref={wrapperRef}
        className="overflow-auto h-full"
        // style={{ maxHeight: "900px" }}
      >
        {isFetching || !tableData ? (
          renderLoading()
        ) : (
          <table className="w-full" ref={tableRef}>
            <thead className="sticky top-0 bg-white z-10">
              {getHeaderGroups().map((headerGroup, i) => (
                <tr key={headerGroup.id} id={headerGroup.id}>
                  {columnActions && (
                    <th className="w-[100px] px-4 py-2 text-left text-xs uppercase tracking-wide text-gray-500 font-semibold border-b border-gray-200">
                      Actions
                    </th>
                  )}
                  {headerGroup.headers.map((column, i) => (
                    <th
                      key={column.id + i}
                      id={column.id + i}
                      scope="col"
                      className="min-w-[100px] px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200"
                    >
                      {flexRender(
                        column.column.columnDef.header,
                        column.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white">
              {getRowModel()?.rows.map((row, i) => (
                <tr
                  key={row.id}
                  className={i % 2 !== 0 ? "bg-white" : "bg-gray-100"}
                >
                  {columnActions && columnActions(row)}
                  {/* <td className="flex relative pr-3">
                    <IconButton
                      key={row.id}
                      onClick={() =>
                        openDialog("userDetailsDialog", row.original)
                      }
                      color="secondary"
                      className="h-full"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        editUser(row.original)
                      }}
                      color="secondary"
                      className="h-full"
                    >
                      <EditIcon />
                    </IconButton>
                  </td> */}
                  {row.getVisibleCells().map((cell, i) => (
                    <td
                      key={cell.id}
                      className="whitespace-nowrap px-3 py-1 text-sm text-black-500"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!isFetching && (
        <div className="bg-white border-t-1 border-gray-300 flex gap-6 py-2 z-30">
          {downloadCSV && (
            <div className="flex gap-4 pl-2">
              <Tooltip title="Download data">
                <IconButton onClick={handleOpenExportMenu}>
                  <GetApp className="text-[#50cdd8]" />
                </IconButton>
              </Tooltip>
              <Menu
                id="admin-dropdown"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                getContentAnchorEl={null}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseExport}
              >
                <MenuItem onClick={() => downloadCSV("allData")}>
                  All Data
                </MenuItem>
                <MenuItem onClick={() => downloadCSV()}>Current Data</MenuItem>
              </Menu>
            </div>
          )}
          <div className="flex items-center gap-4 pr-8">
            <select
              id="page_size_select"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 mx-2 relative text-center px-2 py-1"
              onChange={(e) => {
                handleChange(e)
                setCurrentPage(0)
              }}
              value={pageSize}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={1}>All</option>
            </select>
            <button
              disabled={!hasPreviousPage}
              onClick={() => {
                setCurrentPage(currentPage - 1)
              }}
              className={
                hasPreviousPage
                  ? "w-6 h-10 bg-transparent text-black text-xs hover:text-[#50cdd8]"
                  : "w-6 h-10 bg-transparent opacity-40 text-black text-xs cursor-default"
              }
            >
              <ChevronLeftIcon />
            </button>
            <p className="whitespace-nowrap">{renderPageCounts()}</p>
            <button
              disabled={!hasNextPage}
              onClick={() => {
                if (currentPage === data.pageParams.length - 1) {
                  fetchNextPage().then(() => setCurrentPage(currentPage + 1))
                } else {
                  setCurrentPage(currentPage + 1)
                }
              }}
              className={
                hasNextPage
                  ? "w-6 h-10 bg-transparent text-black text-xs hover:text-[#50cdd8]"
                  : "w-6 h-10 bg-transparent opacity-40 text-black text-xs cursor-default"
              }
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

BasicTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.object,
  columnActions: PropTypes.func,
  downloadCSV: PropTypes.func,
  isFetching: PropTypes.bool,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  fetchNextPage: PropTypes.func,
  refetch: PropTypes.func,
  hasNextPage: PropTypes.bool,
  pageSize: PropTypes.number,
  setPageSize: PropTypes.func,
  tableRef: PropTypes.string,
}

export default BasicTable
